import { Box, Divider, Skeleton, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React from "react";
import { ParsedResultSet } from "../../workout/utils";
import { ExerciseHistoryItem } from "../../client-workouts/ExerciseHistoryItem";
import { ExerciseTypeSet, JourneyAnchors, Units } from "../../../constants";
import clsx from "clsx";
import dayjs from "dayjs";
import { WorkoutExercise } from "../../workout/types";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    padding: 20,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    gap: theme.spacing(2),
    fontSize: 14,
    maxHeight: 650,
    overflowY: "auto",
  },
  title: {
    fontWeight: 600,
    fontSize: 14,
  },
  completedAt: {
    fontWeight: 500,
    fontSize: 14,
    color: theme.palette.text.secondary,
  },
  historySetsWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  exerciseHistoryItemRoot: {
    paddingLeft: 0,
    marginLeft: 0,
  },
}));

interface IWorkoutHistoryContainer {
  resultSets?: ParsedResultSet[];
  isLoading: boolean;
  exerciseTypeSet: ExerciseTypeSet;
  units: Units;
}

function resultSetToWorkoutExercise(
  resultSet: ParsedResultSet,
  index: number,
  units: Units,
  exerciseTypeSet: ExerciseTypeSet,
): WorkoutExercise {
  return {
    id: `${resultSet.completedAt}-${index}` as unknown as number,
    title: "",
    subtitle: "",
    instructions: "",
    images: [],
    sets: resultSet.sets,
    units,
    typeSet: resultSet.exerciseTypeSet ?? exerciseTypeSet,
    typeExtraMeasurement: resultSet.exerciseTypeExtraMeasurement,
    originalContent: resultSet.originalContent,
  };
}

const WorkoutHistoryContainer = ({
  resultSets = [],
  isLoading,
  exerciseTypeSet,
  units,
}: IWorkoutHistoryContainer) => {
  const s = useStyles();
  const exercises: WorkoutExercise[] = resultSets.map((rs, index) =>
    resultSetToWorkoutExercise(rs, index, units, exerciseTypeSet),
  );

  return (
    <Box className={clsx(s.root, JourneyAnchors.EXERCISE_HISTORY_LOGS)}>
      <Typography className={s.title}>Workout History</Typography>

      {isLoading ? (
        <Skeleton variant="text" animation="wave" />
      ) : resultSets.length === 0 ? (
        <Typography>No results...</Typography>
      ) : (
        <>
          {resultSets.map((resultSet, index) => {
            const { completedAt } = resultSet;
            const exercise = exercises[index];

            return (
              <React.Fragment key={`${completedAt}-${index}`}>
                {completedAt && (
                  <Typography className={s.completedAt}>
                    {dayjs(completedAt).format("MMMM D, YYYY")}
                  </Typography>
                )}

                <Box className={s.historySetsWrapper}>
                  <ExerciseHistoryItem
                    exercise={exercise}
                    exIndex={index}
                    allExercises={exercises}
                    hideHeader
                    userUnits={units}
                    collapseSectionNoLeftMargin
                    classes={{
                      setRoot: s.exerciseHistoryItemRoot,
                    }}
                  />
                </Box>

                {resultSets.length - 1 !== index && <Divider />}
              </React.Fragment>
            );
          })}
        </>
      )}
    </Box>
  );
};

export default WorkoutHistoryContainer;
